/** 走查与失效判据（G5 · validation）。
 *
 * 规格 `docs/design/screens/validation.md`：列表读 `GET /validation` 的 `{<sid>: status}`，
 * 行展开再取 `GET /validation/{sid}`；走查 / 复评 / 判定三个入口发 `POST /validation/{sid}/{action}`
 * （带 `as_of`、`operator`、`reason`，202 后轮询 `GET /jobs/{job_id}`），恢复走 `POST /validation/{sid}/restore`。
 * 触发人与理由由后端落 `paper/events.jsonl`（kind=validation）。本屏不重算指标、不重判阈值。
 */

import { useEffect, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { post, query } from "../../api/client";
import { isRetryable } from "../../api/errors";
import { Card, Kv, Lede, Note, PageHead } from "../../components/Card";
import { Pill } from "../../components/Pill";
import { ErrorState, Loading, QueryState } from "../../components/States";
import { Table } from "../../components/Table";
import { DASH, num, pct, signedMoney, signedPct, ts } from "../../lib/format";
import {
  basisLabel,
  dispositionOf,
  readValidationDetail,
  readValidationList,
  restorable,
  type MetricBlock,
  type ValidationDetail,
  type ValidationRow,
} from "../../lib/validation";

const READ = { retry: (count: number, error: unknown) => count < 2 && isRetryable(error), retryDelay: 1_500 } as const;

export function ValidationScreen(): ReactNode {
  const list = useQuery<ValidationRow[], Error>({ queryKey: ["validation"], queryFn: readValidationList, refetchInterval: 30_000, ...READ });
  const [open, setOpen] = useState<string | null>(null);
  const rows = list.data ?? [];
  return (
    <>
      <PageHead title="走查与失效判据" sub="GET /validation · GET /validation/{sid}" />
      <Lede>
        每条在跑策略的处置状态（正常 / 降权 / 停用 / 需处置），最近一次走查、复评、观察期判定的结论与依据，以及谁按什么理由恢复了受限策略。
        指标按权益口径（B7）由后端算好，本屏不重算。
      </Lede>
      <QueryState status={list.status} error={list.error} what="验证状态" refetch={() => void list.refetch()}>
        <Card title="处置状态" sub="点行展开明细" nopad>
          <Table
            cols={[{ label: "策略" }, { label: "处置" }, { label: "权重系数", num: true }, { label: "原因", wrap: true }, { label: "最近复评" }, { label: "最近评估" }]}
            rows={rows.map(({ strategy_id, status }) => {
              const d = dispositionOf(status);
              const reviewMissing = status.evaluated && status.last_review_utc === null;
              return {
                key: strategy_id,
                selected: open === strategy_id,
                cls: !status.enabled ? "bad" : status.needs_disposition ? "warnrow" : "",
                onClick: () => setOpen(open === strategy_id ? null : strategy_id),
                cells: [
                  strategy_id,
                  <Pill key="d" tone={d.tone}>{d.label}</Pill>,
                  status.evaluated ? num(status.weight_factor, 2) : DASH,
                  status.needs_disposition_reason ?? status.reason ?? status.note ?? DASH,
                  reviewMissing ? <Pill key="r" tone="warn">需处置：未记录复评</Pill> : ts(status.last_review_utc),
                  ts(status.last_evaluated_utc),
                ],
              };
            })}
            empty="验证配置里没有策略"
          />
        </Card>
      </QueryState>
      {open ? <Detail sid={open} /> : null}
      <Actions rows={rows} listFailed={list.status === "error"} />
    </>
  );
}

function Detail({ sid }: { sid: string }): ReactNode {
  const q = useQuery<ValidationDetail, Error>({ queryKey: ["validation", sid], queryFn: () => readValidationDetail(sid), ...READ });
  if (q.status === "pending") return <Loading what={`${sid} 的验证明细`} />;
  if (q.status === "error") return <ErrorState error={q.error} what={`${sid} 的验证明细`} onRetry={() => void q.refetch()} />;
  const d = q.data;
  const w = d.latest_weekly;
  const j = d.latest_judgement;
  return (
    <div className="grid g2" style={{ marginTop: 12 }}>
      <Card title={`走查 · ${sid}`} sub={w ? `周报截至 ${w.as_of}` : "GET /validation/{sid} latest_weekly"}>
        {w === null ? (
          <Note>还没有周报</Note>
        ) : (
          <>
            <Table
              cols={[{ label: "指标" }, { label: "样本内（研究侧）", num: true }, { label: "样本外（纸上盘）", num: true }]}
              rows={metricRows(w.in_sample, w.out_of_sample)}
            />
            <Note>样本外口径：{basisLabel(w.out_of_sample)}；现金变动只作对照。{w.in_sample === null ? "样本内：缺（研究侧净值未提供）。" : ""}</Note>
            <Table
              cols={[{ label: "滚动窗口" }, { label: "窗口收益", num: true }]}
              rows={w.rolling.map((r) => ({ key: r.window_start, cells: [`${r.window_start} – ${r.window_end}`, signedPct(r.rolling_return)] }))}
              empty="样本外交易日不足一个窗口"
            />
          </>
        )}
      </Card>
      <Card title="观察期判定" sub={j ? `判定日 ${j.as_of}` : "latest_judgement"}>
        {j === null ? (
          <Note>还没有判定</Note>
        ) : (
          <>
            {!j.observation_complete ? (
              <Note tone="warn">观察期未满（已观察 {j.observation_start} – {j.observation_end}），判定只作参考</Note>
            ) : null}
            <Table
              cols={[{ label: "判据" }, { label: "值", num: true }, { label: "阈值", num: true }, { label: "结论" }]}
              rows={j.criteria.map((c) => ({
                key: c.criterion,
                cells: [c.criterion, show(c.value), show(c.threshold), <Pill key="p" tone={c.pass ? "ok" : "bad"}>{c.pass ? "通过" : "未通过"}</Pill>],
              }))}
            />
            <Kv
              pairs={[
                ["总结论", <Pill key="t" tone={j.pass ? "ok" : "bad"}>{j.pass ? "通过" : "不通过"}</Pill>],
                ["观察期", `${j.observation_months} 个月 · ${j.observation_start} – ${j.observation_end}`],
                ["参数变更重新计时", j.parameter_reset_date ?? DASH],
              ]}
            />
          </>
        )}
      </Card>
      <Card title="月度复评" sub="latest_review">
        {d.latest_review === null ? (
          <Note>还没有复评记录</Note>
        ) : (
          <Kv
            pairs={[
              ["复评日", d.latest_review.as_of],
              ["结论", <Pill key="c" tone={d.latest_review.conclusion === "通过" ? "ok" : "warn"}>{d.latest_review.conclusion}</Pill>],
              ["说明", d.latest_review.summary_text],
            ]}
          />
        )}
      </Card>
      <Card title="失效证据与处置历史" sub="status.evidence · status.history[]">
        <Table
          cols={[{ label: "命中指标" }, { label: "实际值", num: true }, { label: "比较", mono: true }, { label: "阈值", num: true }, { label: "动作" }]}
          rows={d.status.matched.map((m, i) => ({
            key: `${m.metric}-${i}`,
            cells: [m.metric, num(m.value, 4), m.operator, num(m.threshold, 4), m.action === "disable" ? "停用" : `降权 ×${num(m.factor, 2)}`],
          }))}
          empty="当前没有命中的失效条件"
        />
        <Table
          cols={[{ label: "时间" }, { label: "动作" }, { label: "操作人" }, { label: "理由", wrap: true }]}
          rows={d.status.history.map((h, i) => ({ key: `${h.at_utc}-${i}`, cells: [ts(h.at_utc), ACTION_LABEL[h.action] ?? h.action, h.operator ?? DASH, h.reason] }))}
          empty="没有降权 / 停用 / 恢复记录"
        />
        <Note>状态切换识别尚未实现（strategy-validation「状态切换识别」无判据）。</Note>
      </Card>
    </div>
  );
}

const ACTION_LABEL: Record<string, string> = { downweight: "降权", disable: "停用", hold: "保持", none: "未命中", restore: "恢复" };

function show(value: unknown): string {
  if (typeof value === "number") return num(value, 4);
  if (value === null || value === undefined) return DASH;
  if (typeof value === "object") return Object.entries(value as Record<string, unknown>).map(([k, v]) => `${k}=${String(v)}`).join("，");
  return String(value);
}

function metricRows(inSample: MetricBlock | null, out: MetricBlock): { key: string; cells: ReactNode[] }[] {
  const miss = "缺";
  const pick = (m: MetricBlock | null, f: (x: MetricBlock) => string) => (m === null ? miss : f(m));
  return [
    { key: "pnl", cells: ["净收益（权益口径）", pick(inSample, (m) => signedMoney(m.net_pnl)), signedMoney(out.net_pnl)] },
    { key: "cash", cells: ["现金变动（对照）", pick(inSample, (m) => signedMoney(m.net_pnl_cash)), signedMoney(out.net_pnl_cash)] },
    { key: "cum", cells: ["累计收益", pick(inSample, (m) => signedPct(m.cumulative_return)), signedPct(out.cumulative_return)] },
    { key: "ann", cells: ["年化收益", pick(inSample, (m) => signedPct(m.annualized_return)), signedPct(out.annualized_return)] },
    { key: "dd", cells: ["最大回撤", pick(inSample, (m) => pct(m.max_drawdown)), pct(out.max_drawdown)] },
    { key: "sharpe", cells: ["Sharpe", pick(inSample, (m) => num(m.sharpe, 2)), num(out.sharpe, 2)] },
    { key: "days", cells: ["交易日", pick(inSample, (m) => `${m.trading_days}（${m.start_date} – ${m.end_date}）`), `${out.trading_days}（${out.start_date} – ${out.end_date}）`] },
  ];
}

type Action = "weekly" | "review" | "judge" | "restore";
const ACTIONS: { action: Action; title: string; hint: string }[] = [
  { action: "weekly", title: "走查", hint: "产物 weekly-<as_of>.json" },
  { action: "review", title: "复评", hint: "产物 review-<YYYY-MM>.json，并写 last_review_utc" },
  { action: "judge", title: "判定", hint: "产物 judgement-<as_of>.json" },
  { action: "restore", title: "恢复受限策略", hint: "恢复后权重回到 1.0；只列受限或需处置的策略" },
];

function Actions({ rows, listFailed }: { rows: ValidationRow[]; listFailed: boolean }): ReactNode {
  return (
    <div className="grid g2" style={{ marginTop: 12 }}>
      {ACTIONS.map((a) => (
        <ActionCard
          key={a.action}
          {...a}
          disabled={listFailed}
          strategies={(a.action === "restore" ? rows.filter((r) => restorable(r.status)) : rows).map((r) => r.strategy_id)}
        />
      ))}
    </div>
  );
}

function ActionCard({ action, title, hint, strategies, disabled }: { action: Action; title: string; hint: string; strategies: string[]; disabled: boolean }): ReactNode {
  const client = useQueryClient();
  const [sid, setSid] = useState("");
  const [asOf, setAsOf] = useState("");
  const [operator, setOperator] = useState("");
  const [reason, setReason] = useState("");
  const [jobId, setJobId] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: () => {
      const body: Record<string, string> = { operator: operator.trim(), reason: reason.trim() };
      if (action !== "restore") body["as_of"] = asOf;
      return post<Record<string, unknown>>(`/validation/${encodeURIComponent(sid)}/${action}`, body);
    },
    onSuccess: (result) => {
      setJobId(typeof result["job_id"] === "string" ? result["job_id"] : null);
      void client.invalidateQueries({ queryKey: ["validation"] });
    },
  });
  const missing = !sid || !operator.trim() || !reason.trim() || (action !== "restore" && !asOf);
  function submit(): void {
    if (action === "restore" && !window.confirm(`确认恢复 ${sid}？恢复后权重回到 1.0`)) return;
    mutation.mutate();
  }
  return (
    <Card title={title} sub={hint}>
      <div className="fieldrow">
        <label className="f">
          <span>策略</span>
          <select value={sid} onChange={(e) => setSid(e.target.value)}>
            <option value="">请选择</option>
            {strategies.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </label>
        {action !== "restore" ? (
          <label className="f">
            <span>as_of</span>
            <input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
          </label>
        ) : null}
        <label className="f">
          <span>操作人</span>
          <input value={operator} onChange={(e) => setOperator(e.target.value)} />
        </label>
        <label className="f">
          <span>理由</span>
          <input value={reason} onChange={(e) => setReason(e.target.value)} />
        </label>
      </div>
      <button className="btn primary" disabled={disabled || missing || mutation.isPending} onClick={submit}>
        {mutation.isPending ? "提交中…" : "提交"}
      </button>
      {disabled ? <Note tone="warn">验证状态取不到，入口置灰。</Note> : missing ? <Note tone="warn">请填写必填字段（操作人与理由会随动作落盘）。</Note> : null}
      {mutation.isError ? <Note tone="bad">{mutation.error instanceof Error ? mutation.error.message : String(mutation.error)}</Note> : null}
      {mutation.isSuccess && action === "restore" ? <Note>已恢复，处置历史已追加一行。</Note> : null}
      {jobId ? <JobProgress jobId={jobId} /> : null}
    </Card>
  );
}

function JobProgress({ jobId }: { jobId: string }): ReactNode {
  const client = useQueryClient();
  const q = useQuery<Record<string, unknown>, Error>({
    queryKey: ["jobs", jobId],
    queryFn: () => query<Record<string, unknown>>(`/jobs/${encodeURIComponent(jobId)}`),
    refetchInterval: (state) => (state.state.data?.["status"] === "running" ? 1_000 : false),
    ...READ,
  });
  const status = q.data?.["status"];
  useEffect(() => {
    if (status === "ok" || status === "failed") void client.invalidateQueries({ queryKey: ["validation"] });
  }, [status, client]);
  if (q.status === "error") return <ErrorState error={q.error} what={`任务 ${jobId}`} />;
  return (
    <Note tone={status === "failed" ? "bad" : ""}>
      任务 <code>{jobId}</code>：{status === undefined ? "查询中" : String(status)}
      {status === "failed" ? `；${String(q.data?.["error"] ?? "")}` : ""}
    </Note>
  );
}
